import React, { useEffect, useState } from "react";
import * as url from "../../../../helpers/url_helper";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import axios from "axios";
import ChipsDataTable from "./ChipsDetail/ChipsDataTable";
import { AssignIcon, DeleteIcon } from "../../../../assets/svg";
import { ActionButton } from "../../../../components/Common/button";
import AssignRoomModal from "./AssignRoomModal";

export interface LockControlProps {
  userId: any;
  selectedPremise?: any;
}

type FieldConfig = {
  key: string;
  label: string;
  format?: (v: any) => string;
};

const formatDate = (v: any) =>
  v !== undefined && v !== null && v !== "" && Number(v) !== 0
    ? new Date(Number(v)).toLocaleString()
    : "-";

// IST strings from the API already come formatted; 1970 means "no end date"
const formatEndDateIST = (v: any) =>
  typeof v === "string" && v.startsWith("1970") ? "Permanent" : v;

const extractList = (res: any): any[] => {

  const list = res?.data?.data?.list ?? [];
  return Array.isArray(list) ? list : [];
};

const lockDetailFields: FieldConfig[] = [
  { key: "lockId", label: "Lock ID" },
  { key: "lockName", label: "Lock Name" },
  { key: "lockAlias", label: "Lock Alias" },
  { key: "lockMac", label: "Lock MAC" },
  { key: "modelNum", label: "Model Number" },
  { key: "firmwareRevision", label: "Firmware Revision" },
  { key: "hardwareRevision", label: "Hardware Revision" },
  { key: "electricQuantity", label: "Battery", format: (v) => `${v}%` },
  { key: "hasGateway", label: "Has Gateway" },
  { key: "autoLockTime", label: "Auto Lock Time" },
  { key: "openDirection", label: "Open Direction" },
  { key: "passageMode", label: "Passage Mode" },
  { key: "passageModeAutoUnlock", label: "Passage Mode Auto Unlock" },
  { key: "privacyLock", label: "Privacy Lock" },
  { key: "resetButton", label: "Reset Button" },
  { key: "tamperAlert", label: "Tamper Alert" },
  { key: "lockSound", label: "Lock Sound" },
  { key: "soundVolume", label: "Sound Volume" },
  { key: "noKeyPwd", label: "No Key Password" },
  { key: "date", label: "Date", format: formatDate },
];

const keyboardPwdColumns: FieldConfig[] = [
  { key: "keyboardPwdName", label: "Name" },
  // { key: "keyboardPwd", label: "Password" },
  { key: "keyboardPwdType", label: "Type" },
  { key: "isCustom", label: "Custom" },
  { key: "startDateIST", label: "Start (IST)" },
  { key: "endDateIST", label: "End (IST)", format: formatEndDateIST },
  { key: "sendDateIST", label: "Sent (IST)" },
  { key: "senderUsername", label: "Sender" },
  { key: "receiverUsername", label: "Receiver" },
  { key: "status", label: "Status" },
];

const fingerprintColumns: FieldConfig[] = [
  { key: "fingerprintName", label: "Name" },
  { key: "fingerprintNumber", label: "Fingerprint Number" },
  { key: "fingerprintType", label: "Type" },
  { key: "startDateIST", label: "Start (IST)" },
  { key: "endDateIST", label: "End (IST)", format: formatEndDateIST },
  { key: "createDateIST", label: "Created (IST)" },
  { key: "senderUsername", label: "Sender" },
];

const icCardColumns: FieldConfig[] = [
  { key: "cardName", label: "Name" },
  { key: "cardNumber", label: "Card Number" },
  { key: "cardType", label: "Type" },
  { key: "createDateIST", label: "Created (IST)" },
  { key: "senderUsername", label: "Sender" },
  { key: "status", label: "Status" },
];

interface CountCellProps {
  lockId: any;
  apiPath: string;
  token: string | null;
  iconClass: string;
  title: string;
  color: "primary" | "success" | "warning";
  reloadKey: boolean;
  onClick: () => void;
}

const CountCell = ({
  lockId,
  apiPath,
  token,
  iconClass,
  title,
  color,
  reloadKey,
  onClick,
}: CountCellProps) => {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .post(
        `${url.BASE_URL}${apiPath}`,
        { lockId },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        if (!cancelled) setCount(extractList(res).length);
      })
      .catch(() => {
        if (!cancelled) setCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [lockId, apiPath, token, reloadKey]);

 return (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className={`btn btn-sm d-inline-flex align-items-center justify-content-center gap-1 px-2 py-1 rounded-3 border bg-${color}-subtle`}
    style={{ minWidth: "70px", height: "32px" }}
  >
    <i className={`bx ${iconClass} fs-6 text-${color}`}></i>

    <span
      className={`badge bg-${color} ${
        color === "warning" ? "text-dark" : "text-white"
      } rounded-pill`}
      style={{ fontSize: "11px", padding: "3px 6px" }}
    >
      {count === null ? "-" : count}
    </span>
  </button>
);
};

function LockList({ userId, selectedPremise }: LockControlProps) {
  const selectedRooms = useSelector(
    (state: any) => state.APIResponse.selectedRooms
  );
  const token = localStorage.getItem("Authorization");

  const [isMultiDeleteModalOpen, setMultiDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteData, setDeleteData] = useState<any>();
  const [isReload, setReload] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [assignData, setAssignData] = useState<any>({});
  const [isAssignRoomModalOpen, setIsAssignRoomModalOpen] = useState(false);

  // detail modal states
  const [isDetailModalOpen, setDetailModalOpen] = useState(false);
  const [lockDetail, setLockDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // keyboard password modal states
  const [isKeyModalOpen, setKeyModalOpen] = useState(false);
  const [keyboardPwdList, setKeyboardPwdList] = useState<any[]>([]);
  const [keyboardPwdLoading, setKeyboardPwdLoading] = useState(false);

  // fingerprint modal states
  const [isFingerModalOpen, setFingerModalOpen] = useState(false);
  const [fingerprintList, setFingerprintList] = useState<any[]>([]);
  const [fingerprintLoading, setFingerprintLoading] = useState(false);

  // IC card modal states
  const [isCardModalOpen, setCardModalOpen] = useState(false);
  const [icCardList, setIcCardList] = useState<any[]>([]);
  const [icCardLoading, setIcCardLoading] = useState(false);

  const columns = [
    {
      name: "Device Key",
      selector: (row: any) => row.deviceId || "-",
      sortable: true,
    },
    {
      name: "Name",
      selector: (row: any) => row.name || "-",
      sortable: true,
    },
    {
      name: "	Room Name",
      selector: (row: any) => row.roomName || "-",
      sortable: true,
    },
    {
      name: "Password",
      cell: (row: any) => (
        <CountCell
          lockId={row.id}
          apiPath={url.AIRLOCK_KEY_BORDER_PWD}
          token={token}
          iconClass="bx-lock-open-alt"
          title="Keyboard Passwords"
          color="primary"
          reloadKey={isReload}
          onClick={() => handleKeyClick(row)}
        />
      ),
    },
    {
      name: "FingerPrint",
      cell: (row: any) => (
        <CountCell
          lockId={row.id}
          apiPath={url.AIRLOCK_FINGER_PRINT}
          token={token}
          iconClass="bx-fingerprint"
          title="Fingerprints"
          color="success"
          reloadKey={isReload}
          onClick={() => handleFingerClick(row)}
        />
      ),
    },
    {
      name: "IC Card",
      cell: (row: any) => (
        <CountCell
          lockId={row.id}
          apiPath={url.AIRLOCK_IC_CARD}
          token={token}
          iconClass="bx-credit-card"
          title="IC Cards"
          color="warning"
          reloadKey={isReload}
          onClick={() => handleCardClick(row)}
        />
      ),
    },
    {
      name: "Actions",
      grow: 2,
      cell: (row: any) => (
        <div className="d-flex align-items-center" style={{ gap: "12px" }}>
          <ActionButton
            id="DetailIconButton"
            title="Details"
            icon={<i className="bx bx-info-circle fs-4"></i>}
            onClick={() => handleDetailClick(row)}
          />
          <ActionButton
            id="DeleteIconButton"
            title="Delete"
            icon={<DeleteIcon />}
            onClick={() => handleDeleteClick(row)}
          />
          <ActionButton
            id="AssignIconButton"
            title="Assign Room"
            icon={<AssignIcon />}
            onClick={() => handleAssignClick(row)}
          />
        </div>
      ),
    },
  ];

  const handleRefresh = () => {
    setReload((prev) => !prev);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      handleRefresh();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const deleteLockApiCall = () => {
    setLoading(true);
    const reqObj = {
      lockId: deleteData.id,
    };

    axios
      .delete(`${url.BASE_URL}${url.DELETE_LOCK}`, {
        data: reqObj,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        toast.success(res.data.msg);
        handleRefresh();
        setDeleteModalOpen(false);
        setDeleteData(null);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeleteClick = (rowData: any) => {
    setDeleteModalOpen(!isDeleteModalOpen);
    setDeleteData(rowData);
  };

  const multiDeleteLockApiCall = () => {
    setLoading(true);
    const reqObj = {
      lockId: selectedRows?.map((item) => item._id),
    };
    axios
      .delete(`${url.BASE_URL}${url.DELETE_LOCK}`, {
        data: reqObj,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        toast.success(res.data.msg);
        handleRefresh();
        setMultiDeleteModalOpen(false);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const handleDeleteAll = () => {
    setMultiDeleteModalOpen(!isMultiDeleteModalOpen);
  };

  const handleAssignClick = (row: any) => {
    if (row !== undefined) {
      setIsAssignRoomModalOpen(!isAssignRoomModalOpen);
      setAssignData(row);
    }
  };

  const getLockDetailApiCall = (lockId: any) => {
    const reqObj = { lockId };

    setDetailLoading(true);
    setLockDetail(null);

    axios
      .post(`${url.BASE_URL}${url.AIRLOCK_USER_DETAILS}`, reqObj, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setLockDetail(res.data?.data || res.data);
      })
      .catch((err) => {
        console.error(
          "AIRLOCK_USER_DETAILS error:",
          err?.response?.data || err
        );
      })
      .finally(() => setDetailLoading(false));
  };

  const getKeyboardPwdApiCall = (lockId: any) => {
    const reqObj = { lockId };

    setKeyboardPwdLoading(true);
    setKeyboardPwdList([]);

    axios
      .post(`${url.BASE_URL}${url.AIRLOCK_KEY_BORDER_PWD}`, reqObj, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setKeyboardPwdList(extractList(res));
      })
      .catch((err) => {
        console.error(
          "AIRLOCK_KEY_BORDER_PWD error:",
          err?.response?.data || err
        );
      })
      .finally(() => setKeyboardPwdLoading(false));
  };

  const getFingerprintApiCall = (lockId: any) => {
    const reqObj = { lockId };

    setFingerprintLoading(true);
    setFingerprintList([]);

    axios
      .post(`${url.BASE_URL}${url.AIRLOCK_FINGER_PRINT}`, reqObj, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setFingerprintList(extractList(res));
      })
      .catch((err) => {
        console.error(
          "AIRLOCK_FINGER_PRINT error:",
          err?.response?.data || err
        );
      })
      .finally(() => setFingerprintLoading(false));
  };

  const getIcCardApiCall = (lockId: any) => {
    const reqObj = { lockId };
    console.log("AIRLOCK_IC_CARD payload:", reqObj);

    setIcCardLoading(true);
    setIcCardList([]);

    axios
      .post(`${url.BASE_URL}${url.AIRLOCK_IC_CARD}`, reqObj, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        console.log("AIRLOCK_IC_CARD response:", res.data);
        setIcCardList(extractList(res));
      })
      .catch((err) => {
        console.error("AIRLOCK_IC_CARD error:", err?.response?.data || err);
      })
      .finally(() => setIcCardLoading(false));
  };

  const handleDetailClick = (rowData: any) => {
    setDetailModalOpen(true);
    getLockDetailApiCall(rowData.id);
  };

  const handleKeyClick = (rowData: any) => {
    setKeyModalOpen(true);
    getKeyboardPwdApiCall(rowData.id);
  };

  const handleFingerClick = (rowData: any) => {
    setFingerModalOpen(true);
    getFingerprintApiCall(rowData.id);
  };

  const handleCardClick = (rowData: any) => {
    setCardModalOpen(true);
    getIcCardApiCall(rowData.id);
  };

  // reusable list table used by the keyboard password, fingerprint and IC card modals
const renderListTable = (
  list: any[],
  cols: FieldConfig[],
  rowKey: string,
  emptyText: string,
  rowClass?: (item: any) => string,
  cellOverride?: (item: any, key: string) => string | undefined
) =>
  list.length === 0 ? (
    <p className="text-muted text-center mb-0">{emptyText}</p>
  ) : (
    <div className="table-responsive">
      <table className="table table-bordered text-nowrap mb-0">
        <thead className="bg-light">
          <tr>
            {cols.map(({ key, label }) => (
              <th key={key}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {list.map((item: any, index: number) => (
            <tr
              key={item[rowKey] || index}
              className={rowClass ? rowClass(item) : ""}
            >
              {cols.map(({ key, format }) => {
                const override = cellOverride?.(item, key);
                return (
                  <td key={key}>
                    {override !== undefined
                      ? override
                      : item[key] !== undefined && item[key] !== null
                      ? format
                        ? format(item[key])
                        : String(item[key])
                      : "-"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  const renderSpinner = () => (
    <div className="text-center py-4">
      <div className="spinner-border text-primary" role="status" />
    </div>
  );

  return (
    <div>
      {isDeleteModalOpen && (
        <ConfirmationModal
          headerTitle="Delete Lock?"
          text="Are you sure you want to delete this Lock ? This action cannot be undone."
          buttonText="Delete"
          onClick={() => {
            deleteLockApiCall();
          }}
          cancleBtnClick={() => setDeleteModalOpen(false)}
          loading={loading}
        />
      )}
      {isMultiDeleteModalOpen && (
        <ConfirmationModal
          headerTitle="Delete Lock?"
          text="Are you sure you want to delete this Lock ? This action cannot be undone."
          buttonText="Delete"
          onClick={() => {
            multiDeleteLockApiCall();
          }}
          cancleBtnClick={() => setMultiDeleteModalOpen(false)}
          loading={loading}
        />
      )}
      {isAssignRoomModalOpen && (
        <AssignRoomModal
          headerTitle="Assign Room"
          idName={"Locklist"}
          assignData={assignData}
          handleRefresh={handleRefresh}
          cancleBtnClick={() => {
            setIsAssignRoomModalOpen(false);
            setAssignData({});
          }}
        />
      )}

      {/* Lock details modal */}
      <Modal
        isOpen={isDetailModalOpen}
        toggle={() => setDetailModalOpen(false)}
        centered
        size="lg"
        scrollable
      >
        <ModalHeader toggle={() => setDetailModalOpen(false)}>
          Lock Details
        </ModalHeader>
        <ModalBody>
          {detailLoading
            ? renderSpinner()
            : lockDetail && (
                <div className="table-responsive">
                  <table className="table table-bordered mb-0">
                    <tbody>
                      {lockDetailFields.map(({ key, label, format }) => (
                        <tr key={key}>
                          <th className="bg-light w-50">{label}</th>
                          <td>
                            {lockDetail[key] !== undefined &&
                            lockDetail[key] !== null
                              ? format
                                ? format(lockDetail[key])
                                : String(lockDetail[key])
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
        </ModalBody>
      </Modal>

      {/* Keyboard passwords modal */}
      <Modal
        isOpen={isKeyModalOpen}
        toggle={() => setKeyModalOpen(false)}
        centered
        size="xl"
        scrollable
      >
        <ModalHeader toggle={() => setKeyModalOpen(false)}>
          Keyboard Passwords
        </ModalHeader>
        <ModalBody>
          {keyboardPwdLoading
            ? renderSpinner()
            : renderListTable(
                keyboardPwdList,
                keyboardPwdColumns,
                "keyboardPwdId",
                "No keyboard passwords found.",
                (item) =>
                  item.keyboardPwdType === "UNKNOWN" ? "opacity-50" : "",
                (item, key) => {
                  if (key === "keyboardPwdName" && item.keyboardPwdType === "UNKNOWN") {
                    return "Expired";
                  }
                  if (key === "keyboardPwd" && item.keyboardPwd) {
                    const pwd = String(item.keyboardPwd);
                    return pwd.slice(0, 2) + "*".repeat(Math.max(pwd.length - 2, 0));
                  }
                  return undefined;
                }
              )}
        </ModalBody>
      </Modal>

      {/* Fingerprints modal */}
      <Modal
        isOpen={isFingerModalOpen}
        toggle={() => setFingerModalOpen(false)}
        centered
        size="xl"
        scrollable
      >
        <ModalHeader toggle={() => setFingerModalOpen(false)}>
          Fingerprints
        </ModalHeader>
        <ModalBody>
          {fingerprintLoading
            ? renderSpinner()
            : renderListTable(
                fingerprintList,
                fingerprintColumns,
                "fingerprintId",
                "No fingerprints found."
              )}
        </ModalBody>
      </Modal>

      {/* IC cards modal */}
      <Modal
        isOpen={isCardModalOpen}
        toggle={() => setCardModalOpen(false)}
        centered
        size="xl"
        scrollable
      >
        <ModalHeader toggle={() => setCardModalOpen(false)}>
          IC Cards
        </ModalHeader>
        <ModalBody>
          {icCardLoading
            ? renderSpinner()
            : renderListTable(
                icCardList,
                icCardColumns,
                "cardId",
                "No IC cards found."
              )}
        </ModalBody>
      </Modal>

      <ChipsDataTable
        apiURL={`${url.BASE_URL}${url.GET_LOCK_LIST}`}
        title="Lock List"
        customStyles={customStyles}
        selectedRoomID={selectedRooms}
        columns={columns}
        isReload={isReload}
        idName={"Locklist"}
        loading={loading}
        setLoading={setLoading}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        userId={userId}
        selectedPremise={selectedPremise}
        expandableRows={false}
        ExpandCollapseIcon={ExpandCollapseIcon}
        handleDeleteAll={handleDeleteAll}
      />
    </div>
  );
}

export default LockList;

export const customStyles: any = {
  tableWrapper: {
    style: {
      minWidth: "1100px",
    },
  },
  subHeader: {
    style: {
      padding: "0px",
      margin: "0px",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#EFF2F7",
      fontWeight: "bold",
      fontSize: "14px",
      borderBottom: "none",
    },
  },
  rows: {
    style: {
      fontSize: "14px",
      color: "#343a40",
      borderBottom: "none",
    },
  },
  cells: {
    style: {
      fontSize: "14px",
      color: "#343a40",
      borderBottom: "none",
    },
  },
};

export const ExpandCollapseIcon = ({ expanded }: { expanded: boolean }) => {
  return (
    <div
      className="d-flex justify-content-center align-items-center  "
      style={{ cursor: "pointer", width: "32px", height: "32px" }}
    >
      <i
        className={`bx ${
          expanded ? "bx-caret-down" : "bx-caret-right"
        } text-dark fs-5`}
      ></i>
    </div>
  );
};